import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  CurrencyDollarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ChartPieIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowUpIcon,
  ArrowDownIcon
} from '@heroicons/react/24/outline';
import { LineChart, DonutChart } from '../charts/Chart';

/**
 * Real-Time Profit Margin Analyzer
 * Advanced profitability tracking and optimization
 */
const ProfitAnalyzer = () => {
  const [profitData, setProfitData] = useState({});
  const [trends, setTrends] = useState([]);
  const [marginAlerts, setMarginAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    generateProfitAnalysis();
    
    // Update every 30 seconds
    const interval = setInterval(generateProfitAnalysis, 30000);
    return () => clearInterval(interval);
  }, []);

  const generateProfitAnalysis = async () => {
    setLoading(true);
    
    try {
      // Fetch real profit analysis data from API
      const response = await axios.get('/api/admin/dashboard/profit-analysis');
      const data = response.data;
      
      setProfitData(data);
      setTrends(data.marginTrends || []);
      
      // Generate margin alerts based on real data
      const alerts = [];
      
      if (data.lowMarginProducts && data.lowMarginProducts.length > 0) {
        const criticalProducts = data.lowMarginProducts.filter(p => p.margin < 15);
        if (criticalProducts.length > 0) {
          alerts.push({
            id: 1,
            type: 'critical',
            title: 'מרווח נמוך קריטי',
            message: `${criticalProducts.length} מוצרים עם מרווח רווח מתחת ל-15%. בדוק מחירי ספקים.`,
            action: 'בדוק מחירים',
            impact: -criticalProducts.reduce((sum, p) => sum + p.revenue, 0) * 0.1
          });
        }
      }
      
      if (data.highMarginProducts && data.highMarginProducts.length > 0) {
        alerts.push({
          id: 2,
          type: 'opportunity',
          title: 'הזדמנות - מוצרים רווחיים',
          message: `${data.highMarginProducts.length} מוצרים עם מרווח גבוה. הגדל השיווק והמלאי.`,
          action: 'הגדל מלאי',
          impact: data.highMarginProducts.reduce((sum, p) => sum + p.revenue, 0) * 0.2
        });
      }

      const shippingShare = data.costBreakdown?.find(c => c.label === 'משלוחים');
      if (shippingShare && data.overview?.totalRevenue && (shippingShare.value / data.overview.totalRevenue) > 0.1) {
        alerts.push({
          id: 3,
          type: 'warning',
          title: 'עלויות משלוחים גבוהה',
          message: `משלוחים מהווים ${((shippingShare.value / data.overview.totalRevenue) * 100).toFixed(1)}% מההכנסות. בדוק הסכמי משלוח.`,
          action: 'בדוק הסכמים',
          impact: -Math.round(shippingShare.value * 0.15)
        });
      }
      
      setMarginAlerts(alerts);
      
    } catch (error) {
      console.error('Error fetching profit analysis:', error);
      setProfitData({ overview: {}, categoryProfitability: [], marginTrends: [], costBreakdown: [], highMarginProducts: [], lowMarginProducts: [] });
      setTrends([]);
      setMarginAlerts([{ id: 'err', type: 'critical', title: 'שגיאה בטעינה', message: error.response?.data?.message || 'לא ניתן לטעון נתוני רווחיות. ודא שהשרת פועל.', action: '', impact: 0 }]);
    }

    setLoading(false);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getMarginColor = (margin) => {
    if (margin >= 50) return 'text-green-600 bg-green-50';
    if (margin >= 30) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up': return <ArrowUpIcon className="h-4 w-4 text-green-500" />;
      case 'down': return <ArrowDownIcon className="h-4 w-4 text-red-500" />;
      default: return <div className="w-4 h-4 rounded-full bg-gray-400" />;
    }
  };

  const getAlertColor = (type) => {
    switch (type) {
      case 'critical': return 'border-red-500 bg-red-50';
      case 'warning': return 'border-yellow-500 bg-yellow-50';
      case 'opportunity': return 'border-green-500 bg-green-50';
      default: return 'border-gray-300 bg-gray-50';
    }
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'critical': return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
      case 'warning': return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />;
      case 'opportunity': return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      default: return <ExclamationTriangleIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white p-6 rounded-lg border border-gray-200 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
              <div className="h-8 bg-gray-200 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <div className="p-2 bg-gradient-to-r from-green-500 to-blue-600 rounded-lg mr-3">
            <ChartPieIcon className="h-6 w-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">מנתח רווחיות בזמן אמת</h3>
            <p className="text-sm text-gray-600">ניתוח מתקדם של מרווחי רווח והחלת אופטימיזציות</p>
          </div>
        </div>
        <div className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
          עודכן לפני זמן קצר
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">רווח גולמי</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(profitData.overview?.grossProfit)}</p>
              <div className="flex items-center mt-2">
                <ArrowTrendingUpIcon className="h-4 w-4 text-green-500 ml-1" />
                <span className="text-sm text-green-600 font-medium">{profitData.overview?.grossMargin}%</span>
              </div>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <CurrencyDollarIcon className="h-8 w-8 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">רווח נקי</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(profitData.overview?.netProfit)}</p>
              <div className="flex items-center mt-2">
                <ArrowTrendingUpIcon className="h-4 w-4 text-green-500 ml-1" />
                <span className="text-sm text-green-600 font-medium">{profitData.overview?.netMargin}%</span>
              </div>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <ArrowTrendingUpIcon className="h-8 w-8 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">שינוי במרווח</p>
              <p className={`text-2xl font-bold ${profitData.overview?.marginChange != null ? (profitData.overview.marginChange >= 0 ? 'text-green-600' : 'text-red-600') : 'text-gray-500'}`}>
                {profitData.overview?.marginChange != null
                  ? `${profitData.overview.marginChange >= 0 ? '+' : ''}${profitData.overview.marginChange}%`
                  : 'אין נתונים'}
              </p>
              <div className="flex items-center mt-2">
                {profitData.overview?.marginChange != null && profitData.overview.marginChange >= 0 && <ArrowUpIcon className="h-4 w-4 text-green-500 ml-1" />}
                {profitData.overview?.marginChange != null && profitData.overview.marginChange < 0 && <ArrowDownIcon className="h-4 w-4 text-red-500 ml-1" />}
                <span className="text-sm text-gray-600">מהחודש הקודם</span>
              </div>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <ArrowUpIcon className="h-8 w-8 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">יחס עלויות</p>
              <p className="text-2xl font-bold text-gray-900">{profitData.overview?.costRatio}%</p>
              <div className="flex items-center mt-2">
                <ArrowTrendingDownIcon className="h-4 w-4 text-red-500 ml-1" />
                <span className="text-sm text-gray-600">מסך ההכנסות</span>
              </div>
            </div>
            <div className="p-3 bg-purple-100 rounded-full">
              <ChartPieIcon className="h-8 w-8 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {marginAlerts.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-md font-semibold text-gray-900">התראות רווחיות</h4>
          {marginAlerts.map((alert) => (
            <div key={alert.id} className={`p-4 rounded-lg border-r-4 ${getAlertColor(alert.type)}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-start">
                  {getAlertIcon(alert.type)}
                  <div className="mr-3">
                    <h5 className="font-semibold text-gray-900">{alert.title}</h5>
                    <p className="text-sm text-gray-700 mt-1">{alert.message}</p>
                    <div className="flex items-center mt-2 space-x-4 space-x-reverse">
                      <button className="text-xs bg-blue-600 text-white px-3 py-1 rounded-full hover:bg-blue-700 transition-colors">
                        {alert.action}
                      </button>
                      <span className={`text-sm font-medium ${alert.impact > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        השפעה: {alert.impact > 0 ? '+' : ''}{alert.impact.toLocaleString('he-IL')}₪
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Charts and Analysis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Margin Trends */}
        <LineChart
          data={trends}
          title="מגמות מרווח רווח (30 ימים)"
          color="#10B981"
          height={300}
        />

        {/* Cost Breakdown */}
        <DonutChart
          data={profitData.costBreakdown || []}
          title="פילוח עלויות"
          centerText={{
            value: formatCurrency(profitData.overview?.totalCosts),
            label: 'סך עלויות'
          }}
        />
      </div>

      {/* Product Profitability Analysis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* High Margin Products */}
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
            <CheckCircleIcon className="h-5 w-5 text-green-500 ml-2" />
            מוצרים עם מרווח גבוה
          </h4>
          <div className="space-y-3">
            {profitData.highMarginProducts?.map((product, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <div>
                  <p className="font-semibold text-gray-900">{product.name}</p>
                  <p className="text-sm text-gray-600">
                    הכנסות: {formatCurrency(product.revenue)} • עלות: {formatCurrency(product.cost)}
                  </p>
                </div>
                <div className={`px-3 py-1 rounded-full text-sm font-medium ${getMarginColor(product.margin)}`}>
                  {product.margin.toFixed(1)}%
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Low Margin Products */}
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-500 ml-2" />
            מוצרים עם מרווח נמוך
          </h4>
          <div className="space-y-3">
            {profitData.lowMarginProducts?.map((product, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                <div>
                  <p className="font-semibold text-gray-900">{product.name}</p>
                  <p className="text-sm text-gray-600">
                    הכנסות: {formatCurrency(product.revenue)} • עלות: {formatCurrency(product.cost)}
                  </p>
                </div>
                <div className={`px-3 py-1 rounded-full text-sm font-medium ${getMarginColor(product.margin)}`}>
                  {product.margin.toFixed(1)}%
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Category Profitability */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h4 className="text-md font-semibold text-gray-900 mb-4">רווחיות לפי קטגוריות</h4>
        <div className="space-y-4">
          {profitData.categoryProfitability?.map((category, index) => (
            <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                {getTrendIcon(category.trend)}
                <div className="mr-3">
                  <p className="font-semibold text-gray-900">{category.label}</p>
                  <p className="text-sm text-gray-600">
                    רווח: {formatCurrency(category.value)} • עלות: {formatCurrency(category.cost)}
                  </p>
                </div>
              </div>
              <div className="text-left">
                <div className={`px-3 py-1 rounded-full text-sm font-medium ${getMarginColor(category.margin)}`}>
                  {category.margin.toFixed(1)}%
                </div>
                <p className="text-xs text-gray-500 mt-1">מרווח רווח</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProfitAnalyzer;
